import { useEffect, useRef, useState } from 'react';
import {
    VideoCameraIcon,
    VideoCameraSlashIcon,
    MicrophoneIcon,
    ComputerDesktopIcon,
    PhoneXMarkIcon,
    UserGroupIcon,
    ChatBubbleLeftRightIcon,
    HandRaisedIcon,
    EllipsisHorizontalIcon,
    ArrowsPointingOutIcon,
    ArrowsPointingInIcon,
    StopIcon,
    UsersIcon,
    SpeakerXMarkIcon,
    QuestionMarkCircleIcon,
    PencilSquareIcon,
    ChevronDownIcon,
    CheckIcon,
} from '@heroicons/react/24/solid';

const EMPTY_DEVICES = {
    mics: [],
    speakers: [],
    cameras: [],
    activeMic: '',
    activeSpeaker: '',
    activeCamera: '',
};

const ControlBtn = ({
    active,
    selected,
    danger,
    hand,
    label,
    onClick,
    children,
    title,
    disabled,
}) => (
    <button
        type="button"
        title={title || label}
        aria-label={title || label}
        onClick={onClick}
        disabled={disabled}
        className={[
            'vc-ctrl-btn',
            active ? 'is-active' : '',
            selected ? 'is-selected' : '',
            danger ? 'is-danger' : '',
            hand ? 'is-hand' : '',
            disabled ? 'is-disabled' : '',
        ].filter(Boolean).join(' ')}
    >
        <span className="vc-ctrl-btn__icon">{children}</span>
        {label ? <span className="vc-ctrl-btn__label">{label}</span> : null}
    </button>
);

const deviceLabel = (device, index, fallback) => (
    device.label || `${fallback} ${index + 1}`
);

const DeviceMenu = ({ sections, labelledBy }) => (
    <div className="vc-device-menu" role="menu" aria-labelledby={labelledBy}>
        {sections.map((section) => (
            <div key={section.title} className="vc-device-menu__section">
                <p className="vc-device-menu__title">{section.title}</p>
                {section.items.length === 0 && (
                    <p className="vc-device-menu__empty">{section.emptyText}</p>
                )}
                {section.items.map((device, index) => {
                    const isActive = device.deviceId === section.activeId;
                    return (
                        <button
                            key={device.deviceId || `${section.title}-${index}`}
                            type="button"
                            role="menuitemradio"
                            aria-checked={isActive}
                            className={`vc-device-menu__item ${isActive ? 'is-active' : ''}`}
                            onClick={() => section.onSelect(device.deviceId)}
                        >
                            <CheckIcon
                                aria-hidden="true"
                                className={`vc-device-menu__check ${isActive ? '' : 'is-hidden'}`}
                            />
                            <span className="vc-device-menu__name">
                                {deviceLabel(device, index, section.fallbackName)}
                            </span>
                        </button>
                    );
                })}
            </div>
        ))}
    </div>
);

const formatElapsed = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    const mm = String(m).padStart(2, '0');
    const ss = String(s).padStart(2, '0');
    if (h > 0) return `${String(h).padStart(2, '0')}:${mm}:${ss}`;
    return `${mm}:${ss}`;
};

export const VideoCallControls = ({
    isHost = false,
    mediaState = {},
    participantCount = 0,
    title = 'Clase en vivo',
    isFullscreen = false,
    subsessionsPanelOpen = false,
    subsessionAlerts = 0,
    canAskForHelp = false,
    whiteboardVisible = false,
    whiteboardActive = false,
    whiteboardPresenting = false,
    whiteboardDisabled = false,
    whiteboardTitle = '',
    devices = EMPTY_DEVICES,
    onSelectMic,
    onSelectSpeaker,
    onSelectCamera,
    onRefreshDevices,
    onToggleAudio,
    onToggleVideo,
    onToggleShare,
    onToggleWhiteboard,
    onToggleFullscreen,
    onToggleSubsessions,
    onAskForHelp,
    onLeave,
    onEndForAll,
    onOpenLiveUsers,
    onOpenChat,
    handRaised = false,
    onToggleHand,
    onMuteAll,
    recordingStatus = 'Stopped',
    recordingBusy = false,
    onToggleRecording,
}) => {
    const [elapsed, setElapsed] = useState(0);
    // 'more' | 'leave' | 'cam' | 'mic' | null
    const [openMenu, setOpenMenu] = useState(null);
    const menuRefs = {
        more: useRef(null),
        leave: useRef(null),
        cam: useRef(null),
        mic: useRef(null),
    };
    const startedAt = useRef(Date.now());

    useEffect(() => {
        const timer = setInterval(() => {
            setElapsed(Math.floor((Date.now() - startedAt.current) / 1000));
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (!openMenu) return undefined;
        const onDoc = (e) => {
            const wrap = menuRefs[openMenu]?.current;
            if (wrap && !wrap.contains(e.target)) setOpenMenu(null);
        };
        const onKey = (e) => {
            if (e.key === 'Escape') setOpenMenu(null);
        };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openMenu]);

    const toggleMenu = (id) => {
        setOpenMenu((current) => {
            const next = current === id ? null : id;
            if (next === 'cam' || next === 'mic') onRefreshDevices?.();
            return next;
        });
    };

    const videoOn = Boolean(mediaState.videoOn);
    const audioOn = Boolean(mediaState.audioOn);
    const sharing = Boolean(mediaState.sharing);
    const isRecording = recordingStatus === 'Recording' || recordingStatus === 'Paused';

    const selectAndClose = (fn) => (deviceId) => {
        setOpenMenu(null);
        fn?.(deviceId);
    };

    return (
        <div className="vc-controls">
            <div className="vc-controls__left">
                <span className="vc-controls__timer">{formatElapsed(elapsed)}</span>
                {isRecording ? (
                    <span className="vc-controls__recording-pill" title="Grabando en Zoom">
                        <span className="vc-controls__recording-dot" aria-hidden="true" />
                        REC
                    </span>
                ) : null}
                <span className="vc-controls__title" title={title}>{title}</span>
            </div>

            <div className="vc-controls__center">
                <ControlBtn label="Chat" onClick={onOpenChat}>
                    <ChatBubbleLeftRightIcon />
                </ControlBtn>

                <ControlBtn label="En vivo" title="Usuarios conectados a la clase" onClick={onOpenLiveUsers}>
                    <UserGroupIcon />
                    {participantCount > 0 && (
                        <span className="vc-ctrl-badge">{participantCount}</span>
                    )}
                </ControlBtn>

                {isHost ? (
                    <ControlBtn
                        label="Silenciar"
                        title="Silenciar a todos los estudiantes"
                        onClick={onMuteAll}
                    >
                        <SpeakerXMarkIcon />
                    </ControlBtn>
                ) : (
                    <ControlBtn
                        label={handRaised ? 'Bajar mano' : 'Levantar mano'}
                        title={handRaised
                            ? 'Bajar la mano'
                            : 'Avisar al profesor que quieres participar'}
                        active={handRaised}
                        hand
                        onClick={onToggleHand}
                    >
                        <HandRaisedIcon />
                    </ControlBtn>
                )}

                {isHost && (
                    <ControlBtn
                        label={isRecording ? 'Detener' : 'Grabar'}
                        title={isRecording ? 'Detener grabación de la sesión' : 'Grabar sesión en Zoom'}
                        active={isRecording}
                        danger={isRecording}
                        disabled={recordingBusy}
                        onClick={onToggleRecording}
                    >
                        <span
                            className={`vc-record-dot${isRecording ? ' is-recording' : ''}`}
                            aria-hidden="true"
                        />
                    </ControlBtn>
                )}

                {isHost && (
                    <ControlBtn
                        label="Salas"
                        title="Salas de subgrupo"
                        selected={subsessionsPanelOpen}
                        onClick={onToggleSubsessions}
                    >
                        <UsersIcon />
                        {subsessionAlerts > 0 && (
                            <span className="vc-ctrl-badge">{subsessionAlerts}</span>
                        )}
                    </ControlBtn>
                )}

                {canAskForHelp && (
                    <ControlBtn
                        label="Ayuda"
                        title="Pedir ayuda al profesor"
                        onClick={onAskForHelp}
                    >
                        <QuestionMarkCircleIcon />
                    </ControlBtn>
                )}

                {whiteboardVisible && (
                    <ControlBtn
                        label="Pizarra"
                        selected={whiteboardActive && !whiteboardPresenting}
                        active={whiteboardPresenting}
                        danger={whiteboardPresenting}
                        disabled={whiteboardDisabled}
                        title={whiteboardTitle || 'Pizarra'}
                        onClick={onToggleWhiteboard}
                    >
                        <PencilSquareIcon />
                    </ControlBtn>
                )}

                <div className="vc-more-wrap" ref={menuRefs.more}>
                    <ControlBtn
                        label="Más"
                        selected={openMenu === 'more'}
                        onClick={() => toggleMenu('more')}
                    >
                        <EllipsisHorizontalIcon />
                    </ControlBtn>
                    {openMenu === 'more' && (
                        <div className="vc-more-menu" role="menu">
                            <button
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                    setOpenMenu(null);
                                    onToggleFullscreen?.();
                                }}
                            >
                                {isFullscreen ? (
                                    <>
                                        <ArrowsPointingInIcon />
                                        Salir de pantalla completa
                                    </>
                                ) : (
                                    <>
                                        <ArrowsPointingOutIcon />
                                        Pantalla completa
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>

                <span className="vc-controls__divider" />

                <div
                    className={`vc-split ${!videoOn ? 'is-off' : ''}`}
                    ref={menuRefs.cam}
                >
                    <ControlBtn
                        label="Cámara"
                        active={!videoOn}
                        title={videoOn ? 'Detener video' : 'Iniciar video'}
                        onClick={onToggleVideo}
                    >
                        {videoOn ? <VideoCameraIcon /> : <VideoCameraSlashIcon />}
                    </ControlBtn>
                    <button
                        type="button"
                        id="vc-cam-chev"
                        className="vc-split__chev"
                        aria-label="Elegir cámara"
                        aria-haspopup="menu"
                        aria-expanded={openMenu === 'cam'}
                        onClick={() => toggleMenu('cam')}
                    >
                        <ChevronDownIcon aria-hidden="true" />
                    </button>
                    {openMenu === 'cam' && (
                        <DeviceMenu
                            labelledBy="vc-cam-chev"
                            sections={[
                                {
                                    title: 'Cámara',
                                    items: devices.cameras,
                                    activeId: devices.activeCamera,
                                    onSelect: selectAndClose(onSelectCamera),
                                    fallbackName: 'Cámara',
                                    emptyText: 'No se detectaron cámaras',
                                },
                            ]}
                        />
                    )}
                </div>

                <div
                    className={`vc-split ${!audioOn ? 'is-off' : ''}`}
                    ref={menuRefs.mic}
                >
                    <ControlBtn
                        label="Micrófono"
                        active={!audioOn}
                        title={audioOn ? 'Silenciar' : 'Activar micrófono'}
                        onClick={onToggleAudio}
                    >
                        <MicrophoneIcon />
                    </ControlBtn>
                    <button
                        type="button"
                        id="vc-mic-chev"
                        className="vc-split__chev"
                        aria-label="Elegir micrófono y altavoz"
                        aria-haspopup="menu"
                        aria-expanded={openMenu === 'mic'}
                        onClick={() => toggleMenu('mic')}
                    >
                        <ChevronDownIcon aria-hidden="true" />
                    </button>
                    {openMenu === 'mic' && (
                        <DeviceMenu
                            labelledBy="vc-mic-chev"
                            sections={[
                                {
                                    title: 'Micrófono',
                                    items: devices.mics,
                                    activeId: devices.activeMic,
                                    onSelect: selectAndClose(onSelectMic),
                                    fallbackName: 'Micrófono',
                                    emptyText: 'No se detectaron micrófonos',
                                },
                                {
                                    title: 'Altavoz',
                                    items: devices.speakers,
                                    activeId: devices.activeSpeaker,
                                    onSelect: selectAndClose(onSelectSpeaker),
                                    fallbackName: 'Altavoz',
                                    emptyText: 'No se detectaron altavoces',
                                },
                            ]}
                        />
                    )}
                </div>

                {isHost && (
                    <ControlBtn
                        label="Comparte"
                        active={sharing}
                        danger={sharing}
                        title={sharing ? 'Dejar de compartir' : 'Compartir pantalla'}
                        onClick={onToggleShare}
                    >
                        {sharing ? <StopIcon /> : <ComputerDesktopIcon />}
                    </ControlBtn>
                )}

                <span className="vc-controls__divider" />

                <div className="vc-leave-wrap" ref={menuRefs.leave}>
                    <button
                        type="button"
                        className={`vc-leave-btn ${isHost ? 'has-menu' : ''}`}
                        aria-haspopup={isHost ? 'menu' : undefined}
                        aria-expanded={isHost ? openMenu === 'leave' : undefined}
                        onClick={() => {
                            if (isHost) toggleMenu('leave');
                            else onLeave?.();
                        }}
                    >
                        <PhoneXMarkIcon className="vc-leave-btn__icon" aria-hidden="true" />
                        <span className="vc-leave-btn__label">Salir</span>
                        {isHost && (
                            <>
                                <span className="vc-leave-btn__sep" aria-hidden="true" />
                                <ChevronDownIcon className="vc-leave-btn__chev" aria-hidden="true" />
                            </>
                        )}
                    </button>
                    {isHost && openMenu === 'leave' && (
                        <div className="vc-leave-menu" role="menu">
                            <button
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                    setOpenMenu(null);
                                    onLeave?.();
                                }}
                            >
                                Salir de la llamada
                            </button>
                            <button
                                type="button"
                                role="menuitem"
                                className="is-danger"
                                onClick={() => {
                                    setOpenMenu(null);
                                    onEndForAll?.();
                                }}
                            >
                                Terminar para todos
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
